import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Alert, CircularProgress, Divider, Grid,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectService, staffService } from '@smartgrow/shared';
import type { CreateProjectRequest } from '@smartgrow/shared/types';
import { CurrencyInput } from '../../components/CurrencyInput';

const today = new Date().toISOString().split('T')[0];

const initialForm: CreateProjectRequest = {
  projectName: '',
  description: '',
  location: '',
  startDate: today,
  endDate: '',
  budget: 0,
  managerId: '',
  supervisorId: undefined,
  notes: '',
};

export const NewProjectPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<CreateProjectRequest>(initialForm);
  const [errors, setErrors] = useState<Partial<Record<keyof CreateProjectRequest, string>>>({});

  const { data: staffData } = useQuery({
    queryKey: ['staff-all'],
    queryFn: () => staffService.getStaff({ size: 200 }),
  });
  const staff = staffData?.content ?? [];
  const managers = staff.filter((s) => s.role === 'ADMIN' || s.role === 'SUPERVISOR');

  const mutation = useMutation({
    mutationFn: (data: CreateProjectRequest) =>
      projectService.createProject({
        ...data,
        budget: Number(data.budget),
        endDate: data.endDate || undefined,
        supervisorId: data.supervisorId || undefined,
        notes: data.notes || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      navigate('/dashboard/projects');
    },
  });

  const validate = () => {
    const e: typeof errors = {};
    if (!form.projectName.trim()) e.projectName = 'Project name is required';
    if (!form.description.trim()) e.description = 'Description is required';
    if (!form.location.trim()) e.location = 'Location is required';
    if (!form.startDate) e.startDate = 'Start date is required';
    if (!form.budget || Number(form.budget) <= 0) e.budget = 'Budget must be greater than 0';
    if (!form.managerId) e.managerId = 'Manager is required';
    if (form.endDate && form.endDate < form.startDate) e.endDate = 'End date must be after start date';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const set = (field: keyof CreateProjectRequest, value: any) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const handleSubmit = () => {
    if (!validate()) return;
    mutation.mutate(form);
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="text">
          Back
        </Button>
        <Typography variant="h4" fontWeight="bold">New Project</Typography>
      </Box>

      <Paper sx={{ p: 3, maxWidth: 800 }}>
        {mutation.isError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to create project. Please try again.
          </Alert>
        )}

        <Typography variant="h6" sx={{ mb: 2 }}>Project Details</Typography>

        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={8}>
            <TextField
              label="Project Name"
              value={form.projectName}
              onChange={(e) => set('projectName', e.target.value)}
              fullWidth
              error={!!errors.projectName}
              helperText={errors.projectName}
              inputProps={{ maxLength: 200 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Location"
              value={form.location}
              onChange={(e) => set('location', e.target.value)}
              fullWidth
              error={!!errors.location}
              helperText={errors.location}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Description"
              multiline
              rows={3}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              fullWidth
              error={!!errors.description}
              helperText={errors.description}
              inputProps={{ maxLength: 2000 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <CurrencyInput
              label="Budget"
              value={form.budget || null}
              onChange={(v) => set('budget', v ?? 0)}
              fullWidth
              error={!!errors.budget}
              helperText={errors.budget}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Start Date"
              type="date"
              value={form.startDate}
              onChange={(e) => set('startDate', e.target.value)}
              fullWidth
              error={!!errors.startDate}
              helperText={errors.startDate}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="End Date (optional)"
              type="date"
              value={form.endDate ?? ''}
              onChange={(e) => set('endDate', e.target.value)}
              fullWidth
              error={!!errors.endDate}
              helperText={errors.endDate}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Project Manager"
              value={form.managerId || ''}
              onChange={(e) => set('managerId', e.target.value)}
              fullWidth
              error={!!errors.managerId}
              helperText={errors.managerId}
            >
              {managers.length ? (
                managers.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.fullName} ({s.role})
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No staff found</MenuItem>
              )}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Supervisor (optional)"
              value={form.supervisorId ?? ''}
              onChange={(e) => set('supervisorId', e.target.value || undefined)}
              fullWidth
            >
              <MenuItem value="">None</MenuItem>
              {managers.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.fullName} ({s.role})
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Notes (optional)"
              multiline
              rows={2}
              value={form.notes ?? ''}
              onChange={(e) => set('notes', e.target.value)}
              fullWidth
              inputProps={{ maxLength: 1000 }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={() => navigate('/dashboard/projects')} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} /> : null}
          >
            Create Project
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
